import type LogRocketSDK from 'logrocket';
let LogRocket: typeof LogRocketSDK | undefined;
let loading: Promise<void> | null = null;
import { isPasswordRecoveryPath, isPasswordRecoveryRequest } from '@/features/auth/password-recovery';
import { isOAuthConsentPath, isOAuthConsentRequest } from '@/features/oauth/privacy';
import { useAuthStore } from '@/store/auth-store';
import { isPrivatePlatformRequest } from '@/features/agent-platform/telemetry';
import { isPublicViewerLocation } from '@/features/locale/session';

/**
 * LogRocket session capture for prod debugging.
 *
 * Hardcoded for now — once we add per-environment config we should gate this
 * behind `import.meta.env.PROD` and pull the app ID from VITE_LOGROCKET_ID.
 *
 * Project: blazeteams-ui-v2 (https://app.logrocket.com/78kcgo/blazeteamsv2)
 */
const APP_ID = '78kcgo/blazeteamsv2';

let initialized = false;
let identifiedUserId: string | null = null;

interface IdentifyableUser {
  id?: string;
  name?: string;
  email?: string;
  role?: string;
}

function identifyUser(user: IdentifyableUser) {
  if (isPublicViewerLocation() || isOAuthConsentPath(window.location.pathname)) return;
  if (!user.id || user.id === identifiedUserId) return;
  LogRocket?.identify(user.id, {
    name: user.name ?? '',
    email: user.email ?? '',
    role: user.role ?? '',
  });
  identifiedUserId = user.id;
}

/**
 * Initialize once. Safe to call multiple times — subsequent calls are no-ops.
 * Subscribes to the Zustand auth store so the session reflects the *current*
 * user across login, token refresh, profile updates, and logout — without
 * requiring login flows to remember to call identify themselves.
 */
export function initLogRocket(): Promise<void> {
  if (initialized) return Promise.resolve();
  if (typeof window === 'undefined') return Promise.resolve();
  // Recovery URLs contain a one-time credential; never record this document.
  if (isOAuthConsentPath(window.location.pathname) || isPasswordRecoveryPath(window.location.pathname) || /^\/magic-login\/?$/.test(window.location.pathname) || isPublicViewerLocation()) return Promise.resolve();

  if (loading) return loading;
  // The SDK import itself injects its CDN script. Gate loading, not just init.
  loading = import('logrocket').then(({ default: sdk }) => {
    if (isOAuthConsentPath(window.location.pathname) || isPasswordRecoveryPath(window.location.pathname) || /^\/magic-login\/?$/.test(window.location.pathname) || isPublicViewerLocation()) return;
    LogRocket = sdk;
    initialized = true;
    sdk.init(APP_ID, {
      dom: { privateAttributeBlocklist: ['data-private'] },
      network: {
        requestSanitizer: request => (isOAuthConsentRequest(request.url) || isPrivatePlatformRequest(request.url) || /\/(?:agents|plugins|plugin-bridge|plugin-events|mcp\/connections|settings\/api-tokens|integrations\/addons|webhooks|agent-bots|inbox\/channels\/[^/?#]+\/agent-bot)(?:\/|[?#]|$)/i.test(request.url || '') || request.url.includes('/tiktok') || request.url.includes('tiktok.com') || isPasswordRecoveryRequest(request.url) || /\/auth\/(?:refresh|login|register|switch-tenant)(?:[/?#]|$)/i.test(request.url || '') || /(?:\/recordings\/[^/?]+\/access(?:[?#]|$)|[?&](?:X-Amz-Signature|access_token)=)|\/auth\/magic-login(?:[?#]|$)|\/public\/(?:csat|meetings)\//.test(request.url || '')) ? null : request,
        responseSanitizer: response => (isOAuthConsentRequest(response.url) || isPrivatePlatformRequest(response.url) || /\/(?:agents|plugins|plugin-bridge|plugin-events|mcp\/connections|settings\/api-tokens|integrations\/addons|webhooks|agent-bots|inbox\/channels\/[^/?#]+\/agent-bot)(?:\/|[?#]|$)/i.test(response.url || '') || response.url?.includes('/tiktok') || response.url?.includes('tiktok.com') || isPasswordRecoveryRequest(response.url) || /\/auth\/(?:refresh|login|register|switch-tenant)(?:[/?#]|$)/i.test(response.url || '') || /(?:\/recordings\/[^/?]+\/access(?:[?#]|$)|[?&](?:X-Amz-Signature|access_token)=)|\/auth\/magic-login(?:[?#]|$)|\/public\/(?:csat|meetings)\//.test(response.url || '')) ? null : response,
      },
    });

    // Identify the user already in the store (e.g. on hard refresh, the
    // persisted Zustand state rehydrates *before* we get here, so user is
    // already populated).
    const current = useAuthStore.getState().user;
    if (current) identifyUser(current);

    // Re-identify whenever the user changes. We compare by id so login-then-
    // edit-profile won't keep firing identify().
    useAuthStore.subscribe((state) => {
      if (state.user) {
        identifyUser(state.user);
      } else if (identifiedUserId !== null) {
        // Logout: drop the id so the next login re-identifies. LogRocket has no
        // public "anonymize" API for the current session — the next session
        // (next page reload) will be anonymous until identify is called again.
        identifiedUserId = null;
      }
    });
  }).catch(() => { /* Optional diagnostics never block app startup. */ }).finally(() => { loading = null; });
  return loading;
}

/**
 * Surface a manual error to LogRocket. Most errors are auto-captured (console,
 * unhandled promise rejections, network failures) but call this for caught
 * errors that you still want surfaced — e.g. mutation onError handlers where
 * you swallow the error to show a toast.
 */
export function captureError(err: unknown, extra?: Record<string, string | number | boolean>) {
  if (!initialized || !LogRocket || isPublicViewerLocation() || isOAuthConsentPath(window.location.pathname)) return;
  if (err instanceof Error) {
    LogRocket.captureException(err, { extra });
  } else {
    LogRocket.captureMessage(String(err), { extra });
  }
}
